import { Injectable, signal } from '@angular/core';
import { OfflineSyncStatus } from '@shared/constants/offline.constants';
import { NetworkStatusService } from '@shared/services/offline/network-status.service';

export type OfflineBannerVariant = 'offline' | 'online-sync' | 'sync-failed' | 'hidden';

@Injectable({ providedIn: 'root' })
export class OfflineUiService {
  private readonly globalBannerVisible = signal(false);
  private readonly globalBannerMessage = signal('');
  private readonly formSyncStatus = signal<OfflineSyncStatus | null>(null);
  private readonly formBannerVariant = signal<OfflineBannerVariant>('hidden');
  private readonly syncInProgress = signal(false);

  readonly showGlobalBanner = this.globalBannerVisible.asReadonly();
  readonly globalBannerText = this.globalBannerMessage.asReadonly();
  readonly currentFormSyncStatus = this.formSyncStatus.asReadonly();
  readonly formBanner = this.formBannerVariant.asReadonly();
  readonly isSyncing = this.syncInProgress.asReadonly();

  constructor(private readonly networkStatus: NetworkStatusService) {
    this.refreshGlobalBanner();
  }

  refreshGlobalBanner(): void {
    const offline = !this.networkStatus.isOnline();
    this.globalBannerVisible.set(offline);
    this.globalBannerMessage.set(
      offline
        ? $localize`:@@offlineGlobalBannerMessage:You are offline. Some features may be unavailable.`
        : '',
    );
  }

  setFormSyncStatus(status: OfflineSyncStatus | null): void {
    this.formSyncStatus.set(status);
    this.updateFormBanner(status);
  }

  setSyncInProgress(inProgress: boolean): void {
    this.syncInProgress.set(inProgress);
  }

  private updateFormBanner(status: OfflineSyncStatus | null): void {
    if (!status) {
      this.formBannerVariant.set('hidden');
      return;
    }

    if (status === 'sync-failed') {
      this.formBannerVariant.set('sync-failed');
      return;
    }

    if (status === 'saved-locally' || status === 'draft') {
      this.formBannerVariant.set(this.networkStatus.isOnline() ? 'online-sync' : 'offline');
      return;
    }

    if (!this.networkStatus.isOnline()) {
      this.formBannerVariant.set('offline');
      return;
    }

    this.formBannerVariant.set('hidden');
  }
}
